import { useState } from 'react';
import { useLocation } from 'react-router';

import { setGlobalAnalyticsParam } from './global-params.ts';
import { sendUserPortalEvent } from './unified.ts';
import { useViewTracking } from './view-tracking.ts';

const CLICKABLE_SELECTOR = [
  'button',
  'a[href]',
  'summary',
  '[role="button"]',
  '[role="link"]',
  '[role="tab"]',
  '[role="checkbox"]',
  '[role="switch"]',
  '[role="radio"]',
  '[role="menuitem"]',
  '[role="menuitemcheckbox"]',
  '[role="menuitemradio"]',
  '[role="option"]',
].join(',');

// GA4 truncates parameter values longer than 100 characters
const MAX_TEXT_LENGTH = 100;

const getClickContext = (el: HTMLElement, root: Element) => {
  let ctx: Record<string, any> = {};
  for (let node: HTMLElement | null = el; node && node !== root; node = node.parentElement) {
    const clickCtx = node.dataset.clickCtx;
    // Inner context overrides outer context
    if (clickCtx) ctx = { ...JSON.parse(clickCtx), ...ctx };
  }
  return ctx;
};

/** State before the click is applied, e.g. `false` for a switch that is being turned on. */
const getPrevState = (el: HTMLElement) => {
  if (el.parentElement instanceof HTMLDetailsElement && el.nodeName === 'SUMMARY') {
    return el.parentElement.open ? 'open' : 'closed';
  }
  return (
    el.getAttribute('aria-checked') ??
    el.getAttribute('aria-expanded') ??
    el.getAttribute('aria-selected') ??
    undefined
  );
};

const getText = (el: HTMLElement) => {
  const text = el.textContent.replace(/\s+/g, ' ').trim() || el.getAttribute('aria-label') || '';
  return text.slice(0, MAX_TEXT_LENGTH) || undefined;
};

const createPageView = (pathname: string) => ({ id: crypto.randomUUID(), pathname });

export const AutoTracking = (props: { children: React.ReactNode }) => {
  const location = useLocation();

  const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (!(event.target instanceof Element)) return;

    const el = event.target.closest<HTMLElement>(CLICKABLE_SELECTOR);
    // Portaled content (dialogs, menus) isn't a DOM descendant, only skip matches above the root
    if (!el || el.contains(event.currentTarget)) return;

    const text_content = getText(el);
    const element_id = el.dataset.testid;
    const href = el.getAttribute('href') ?? undefined;
    // Without any of these there is no way to tell what was clicked
    if (!text_content && !element_id && !href) return;

    sendUserPortalEvent('click', {
      ...getClickContext(el, event.currentTarget),
      text_content,
      element_name: el.nodeName.toLowerCase(),
      element_role: el.getAttribute('role') ?? undefined,
      element_id,
      element_prev_state: getPrevState(el),
      href,
    });
  };

  const [pageView, setPageView] = useState(() => createPageView(location.pathname));
  if (pageView.pathname !== location.pathname) {
    setPageView(createPageView(location.pathname));
  }
  // Set during render, so pages rendering below already see it, including their mount effects
  setGlobalAnalyticsParam('page_view_id', pageView.id);

  useViewTracking({
    view: { id: pageView.id, params: { page_path: pageView.pathname } },
    onView: (view) => {
      sendUserPortalEvent('page_view', {
        ...view.params,
        page_previous_path: view.previousParams?.page_path,
        page_title: document.title,
        referrer: document.referrer,
        page_view_id: view.id,
      });
    },
    onEngagement: (view, engagement) => {
      sendUserPortalEvent('page_engagement', {
        ...view.params,
        page_view_id: view.id,
        engagement_time_ms: engagement.timeMs,
        reason: engagement.reason,
      });
    },
  });

  return (
    <div className="contents" onClick={handleClick}>
      {props.children}
    </div>
  );
};

/**
 * Adds extra context for **automatic** click event tracking.
 * This doesn't affect any other events, only generic clicks.
 */
export const ClickEventContext = (props: {
  children: React.ReactNode;
  ctx: Record<string, any>;
}) => {
  return (
    <div data-click-ctx={JSON.stringify(props.ctx)} className="contents">
      {props.children}
    </div>
  );
};
